import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import {toast} from 'react-toastify';
import {Field, inputSx} from './partnerFormField';

/**
 * Shows a freshly generated client secret.
 *
 * This is the only moment the plaintext exists outside the partner's hands: the
 * server stores a scrypt hash and cannot produce it again. Closing the dialog is
 * therefore deliberately gated behind an acknowledgement, and the copy button is
 * more prominent than the close button.
 */
const SecretRevealDialog = (props) => {
  const [acknowledged, setAcknowledged] = useState(false);

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast(`${label} copied.`, {type: 'success'});
    } catch (e) {
      toast('Could not copy. Select the text and copy manually.', {type: 'warning'});
    }
  };

  const handleClose = () => {
    setAcknowledged(false);
    props.onClose();
  };

  return (
    <Dialog open={props.open} maxWidth={'md'} fullWidth onClose={() => {}}>
      <DialogTitle>Credentials for {props.partnerName}</DialogTitle>
      <DialogContent>
        <Alert severity={'warning'} sx={{mb: 2}}>
          This secret is shown once and is not stored in a readable form. If it
          is lost, generate a new one &mdash; it cannot be recovered.
        </Alert>

        {props.rotated && (
          <Alert severity={'info'} sx={{mb: 2}}>
            The previous secret keeps working until{' '}
            {props.previousSecretValidUntil
              ? new Date(props.previousSecretValidUntil).toLocaleString()
              : 'tomorrow'}
            , so the partner can switch over without downtime.
          </Alert>
        )}

        <Box display={'flex'} alignItems={'flex-end'} gap={1} mb={2}>
          <Field label={'Client ID'}>
            <TextField
              value={props.clientId || ''}
              fullWidth
              InputProps={{readOnly: true}}
              size={'small'}
              sx={inputSx}
            />
          </Field>
          <Tooltip title={'Copy client ID'}>
            <IconButton onClick={() => copy(props.clientId, 'Client ID')}>
              <ContentCopyIcon/>
            </IconButton>
          </Tooltip>
        </Box>

        <Box display={'flex'} alignItems={'flex-end'} gap={1}>
          <Field label={'Client Secret'}>
            <TextField
              value={props.clientSecret || ''}
              fullWidth
              InputProps={{readOnly: true}}
              size={'small'}
              sx={inputSx}
            />
          </Field>
          <Tooltip title={'Copy secret'}>
            <IconButton onClick={() => copy(props.clientSecret, 'Client secret')}>
              <ContentCopyIcon/>
            </IconButton>
          </Tooltip>
        </Box>

        <Typography variant={'body2'} sx={{mt: 2, color: 'text.secondary'}}>
          Send these to the partner over a channel that expires &mdash; not an
          email body or a ticket comment.
        </Typography>

        <Box mt={2}>
          <label style={{display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer'}}>
            <input
              type={'checkbox'}
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
            />
            <Typography variant={'body2'}>
              I have copied the secret somewhere safe.
            </Typography>
          </label>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant={'contained'} disabled={!acknowledged}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

SecretRevealDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  partnerName: PropTypes.string,
  clientId: PropTypes.string,
  clientSecret: PropTypes.string,
  rotated: PropTypes.bool,
  previousSecretValidUntil: PropTypes.string,
};

SecretRevealDialog.defaultProps = {
  partnerName: '',
  clientId: '',
  clientSecret: '',
  rotated: false,
  previousSecretValidUntil: undefined,
};

export default SecretRevealDialog;
